import { FormField, Input, Paragraph, Skeleton } from '@frontapp/ui-kit';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useReadLocalStorage } from 'usehooks-ts';
import Profile from './Profile';
import chargebeeBookmarklet from './bookmarklets/chargebeeBookmarklet';
import loginBookmarklet from './bookmarklets/loginBookmarklet';
import rollbarBookmarklet from './bookmarklets/rollbarBookmarklet';
import type { ProfileResponse } from './types';
import { ApiClientError } from './utils/ApiClient';
import { useFrontContext } from './utils/front';
import { useInternalClient } from './utils/useInternalClient';

type Props = {
  onLogout: () => void;
};

// Short SHA of the commit that built this bundle (see bin/commit-sha.sh).
// Shown in the Autologin link label, which becomes the bookmark's name when
// dragged to the toolbar, so agents can tell which version they hold.
const COMMIT_SHA = process.env.REACT_APP_COMMIT_SHA || 'unknown';

function App({ onLogout }: Props) {
  const token = useReadLocalStorage<string>('customerToken')!;
  const context = useFrontContext();
  const client = useInternalClient();

  const conversationEmail =
    context?.type === 'singleConversation'
      ? context.conversation.recipient?.handle
      : '';

  const [currentSearch, setCurrentSearch] = useState<string>(
    conversationEmail || '',
  );

  const [loading, setLoading] = useState(false);

  const [response, setResponse] = useState<ProfileResponse | undefined>();

  const performSearch = useCallback(
    async (info: string) => {
      try {
        setLoading(true);
        const cleanInfo = info.replaceAll(/\s/g, '');
        setResponse(await client.getProfile(cleanInfo));
      } catch (e) {
        if (e instanceof ApiClientError && e.response.status === 401) {
          onLogout();
          return;
        }

        throw e;
      } finally {
        setLoading(false);
      }
    },
    [client, onLogout],
  );

  useEffect(() => {
    if (!conversationEmail) {
      return;
    }

    setCurrentSearch(conversationEmail);
    performSearch(conversationEmail);
  }, [conversationEmail, performSearch]);

  async function handleSearch(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    await performSearch(currentSearch);
  }

  if (
    context &&
    context.type !== 'singleConversation' &&
    context.type !== 'noConversation'
  ) {
    return <div>Don't know how to handle context {context.type}</div>;
  }

  return (
    <>
      <form onSubmit={handleSearch}>
        <FormField
          hint="Search by prefix: `account:<id>`, `project:<id>`, `org:<id>`, `chargebee_customer:<id>` or `chargebee_subscription:<id>`, `invoice:<number>`. If you don't use a prefix, it will search in account's email, organition's name and project's internal subdomain."
          label="Search"
        >
          <Input
            name="info"
            type="text"
            shouldFocus={true}
            iconName="Search"
            placeholder=""
            value={currentSearch}
            onChange={(value) => setCurrentSearch(value)}
          />
        </FormField>
      </form>
      <div style={{ marginTop: '20px' }}>
        {loading ? (
          <Skeleton height={100} />
        ) : response ? (
          <Profile response={response} />
        ) : undefined}
      </div>
      <div style={{ marginTop: '20px' }}>
        <Paragraph>Drag these to your browser's bookmarks toolbar!</Paragraph>
        <div style={{ marginTop: '10px' }}>
          <Paragraph>
            <a className="Bookmarklet" href={loginBookmarklet(token)}>
              Autologin (version {COMMIT_SHA})
            </a>
            &nbsp;
            <a className="Bookmarklet" href={chargebeeBookmarklet()}>
              Chargebee Info
            </a>
            &nbsp;
            <a className="Bookmarklet" href={rollbarBookmarklet(token)}>
              Rollbar
            </a>
          </Paragraph>
        </div>
      </div>
    </>
  );
}

export default App;
